import { Mui, MuiX, MuiIcons, DateFns } from '@osu/react-ui';
import React, { useState, useEffect } from 'react';
import { SEARCH_OPERATORS } from '../../util/constants';
import { useGetDistinctValuesForSearchQuery } from '../../services/graphql/api';

const AttributeRow = ({ attributesData, deleteRow, updateRowData, rowIndex, onSearch, initialData }) => {
    // Initialize state with proper value processing for dates
    const getInitialValue = () => {
        if (!initialData?.selectedValue) return null;
        
        const attributeType = initialData.selectedAttribute?.type?.toLowerCase();
        const operatorKey = initialData.operator?.KEY;
        const isRangeOperator = operatorKey === 'BT' || initialData.operator?.LABEL?.toLowerCase().includes('between');
        
        // For date ranges with between operator
        if ((attributeType === 'date' || attributeType === 'datetime') && isRangeOperator) {
            if (typeof initialData.selectedValue === 'object' && !Array.isArray(initialData.selectedValue)) {
                return {
                    start: initialData.selectedValue.start ? new Date(initialData.selectedValue.start) : null,
                    end: initialData.selectedValue.end ? new Date(initialData.selectedValue.end) : null
                };
            }
            return { start: null, end: null };
        }
        
        // For single date values
        if (attributeType === 'date' || attributeType === 'datetime') {
            return initialData.selectedValue ? new Date(initialData.selectedValue) : null;
        }
        
        // For text/string arrays
        if (attributeType === 'text' || attributeType === 'string') {
            return Array.isArray(initialData.selectedValue) ? initialData.selectedValue : [];
        }
        
        return initialData.selectedValue;
    };
    
    const [operator, setOperator] = useState(initialData?.operator ?? '');
    const [selectedValue, setSelectedValue] = useState(getInitialValue());
    const [selectedAttribute, setSelectedAttribute] = useState(initialData?.selectedAttribute ?? null);
    const [attributeInputValue, setAttributeInputValue] = useState('');
    const [valueInputValue, setValueInputValue] = useState(''); // Add separate state for value input
    const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Track dropdown state
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
    const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);

     // Check if the operator is "between" for date range logic
    const isBetweenOperator = operator?.KEY === 'BT' || operator?.LABEL?.toLowerCase().includes('between');

    const { data: distinctValuesData, isFetching: distinctValuesLoading } = useGetDistinctValuesForSearchQuery(selectedAttribute?.key, {
        skip: !selectedAttribute?.key, // Skip the query if no attribute is selected
    });

    // Get available operators based on attribute type
    const getAvailableOperators = () => {
        if (!selectedAttribute?.type) return [];

        const attributeType = selectedAttribute.type.toLowerCase();

        // Map attribute types to SEARCH_OPERATORS keys
        if (attributeType === 'text' || attributeType === 'string') {
            return SEARCH_OPERATORS.text || [];
        } else if (['number', 'int', 'integer', 'float', 'decimal', 'long'].includes(attributeType)) {
            return SEARCH_OPERATORS.number || [];
        } else if (attributeType === 'date' || attributeType === 'datetime') {
            return SEARCH_OPERATORS.date || [];
        } else if (attributeType === 'bool' || attributeType === 'boolean') {
            return SEARCH_OPERATORS.text || []; // Boolean only uses equals
        }

        return SEARCH_OPERATORS.text || []; // fallback to text operators
    };

    // Get default operator based on attribute type
    const getDefaultOperator = (attributeType) => {
        const type = attributeType?.toLowerCase();

        if (type === 'bool' || type === 'boolean') {
            // For boolean, find the "Equal To" operator
            return SEARCH_OPERATORS.text?.find(op => op.KEY === 'EQ') || '';
        } else if (type === 'text' || type === 'string') {
            // For text, find the "Contains" operator
            return SEARCH_OPERATORS.text?.find(op => op.KEY === 'EQ') || '';
        } else if (type === 'date' || type === 'datetime') {
            // For date, find the "After" operator (or you could use a different default)
            return SEARCH_OPERATORS.date?.find(op => op.KEY === 'GT') || '';
        } else if (['number', 'int', 'integer', 'float', 'decimal', 'long'].includes(type)) {
            // For numbers, default to "Equal To"
            return SEARCH_OPERATORS.number?.find(op => op.KEY === 'EQ') || '';
        }

        // Fallback: default to "Equal To" for any other type
        return (
            SEARCH_OPERATORS.text?.find(op => op.KEY === 'EQ') ||
            SEARCH_OPERATORS.number?.find(op => op.KEY === 'EQ') ||
            ''
        );
    };

    // Check if operator should be disabled (for boolean fields)
    const isOperatorDisabled = () => {
        const attributeType = selectedAttribute?.type?.toLowerCase();
        return selectedAttribute === null || attributeType === 'bool' || attributeType === 'boolean';
    };

    // Format value for sending to backend
    const formatValueForBackend = () => {
        const attributeType = selectedAttribute?.type?.toLowerCase();

        if (isBetweenOperator) {
            if (attributeType === 'date' || attributeType === 'datetime') {
                // Format date range for backend as proper JSON object
                if (selectedValue?.start && selectedValue?.end) {
                    return {
                        start: selectedValue?.start,
                        end: selectedValue?.end
                    };
                }
                return null;
            } else if (['number', 'int', 'integer', 'float', 'decimal', 'long'].includes(attributeType)) {
                // Format number range for backend as proper JSON object
                if (selectedValue?.start !== null && selectedValue?.start !== '' &&
                    selectedValue?.end !== null && selectedValue?.end !== '') {
                    return {
                        start: parseFloat(selectedValue?.start),
                        end: parseFloat(selectedValue?.end)
                    };
                }
                return null;
            }
        }

        // For single values
        if (attributeType === 'date' || attributeType === 'datetime') {
            return selectedValue;
        }

        if (attributeType === 'bool' || attributeType === 'boolean') {
            if (selectedValue === 'true' || selectedValue === true) return true;
            if (selectedValue === 'false' || selectedValue === false) return false;
            return "";
        }

        // ADD THIS: Convert single number values to actual numbers
        if (['number', 'int', 'integer', 'float', 'decimal', 'long'].includes(attributeType)) {
            return selectedValue === '' ? null : parseFloat(selectedValue);
        }

        // For text/string fields with multiple values, return array
        if ((attributeType === 'text' || attributeType === 'string') && Array.isArray(selectedValue)) {
            return selectedValue.length > 0 ? selectedValue : null;
        }

        return selectedValue;
    };

    // Update parent component whenever any value changes
    useEffect(() => {
        const valueToSend = formatValueForBackend();

        updateRowData(rowIndex, {
            selectedAttribute,
            operator,
            selectedValue: valueToSend
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAttribute, operator, rowIndex, selectedValue]);

    const handleAttributeChange = (event, newValue) => {
        setSelectedAttribute(newValue);
        const newType = newValue?.type?.toLowerCase();
         // Initialize selectedValue based on new attribute type
        if (newType === 'date' || newType === 'datetime') {
            setSelectedValue(null); 
        }  else if (['number', 'int', 'integer', 'float', 'decimal', 'long'].includes(newType)) {
            setSelectedValue(''); // this removed a warning about NaN cannot be parsed
        } else {
            setSelectedValue([]);
        }
        setValueInputValue(''); // Reset value input

        // Set default operator based on attribute type
        const defaultOperator = getDefaultOperator(newValue?.type);
        setOperator(defaultOperator);
    };

    const handleAttributeInputChange = (event, newInputValue) => {
        setAttributeInputValue(newInputValue);
    };

    const handleOperatorChange = (e) => { 
        setOperator(e.target.value);
        const attributeType = selectedAttribute?.type?.toLowerCase();
        
        // Handle date/datetime attributes
        if (attributeType === 'date' || attributeType === 'datetime') {
            if (isBetweenOperator) {
                setSelectedValue({ start: '', end: '' });
            } else {
                setSelectedValue('');
            }
        }
        // Handle text/string attributes
        else if (attributeType === 'text' || attributeType === 'string') {
            setSelectedValue([]);
        }
        // Handle number attributes
        else if (['number', 'int', 'integer', 'float', 'decimal', 'long'].includes(attributeType)) {
            // Similar logic for number ranges
            if (isBetweenOperator) {
                setSelectedValue({ start: '', end: '' });
            } else {
                setSelectedValue('');
            }
        }
        // Handle other types
        else {
            setSelectedValue(null);
        }   
        setValueInputValue('');
    };

    const handleValueChange = (event, newValue) => {
        // For text/string fields, handle multiple values
        const attributeType = selectedAttribute?.type?.toLowerCase();
        if (attributeType === 'text' || attributeType === 'string') {
            setSelectedValue(newValue || []);
        } else {
            setSelectedValue(newValue || '');
        }
    };

    const handleValueInputChange = (event, newInputValue) => {
        // Update the input value state
        setValueInputValue(newInputValue);
    };

    const handleKeyDown = (event) => {
        // Handle Enter key for adding freeform text or triggering search
        if (event.key === 'Enter') {
            if (valueInputValue.trim()) {
                // If there's text being entered, add it as a chip
                const inputValue = valueInputValue.trim();

                // Add the freeform text as a new chip
                setSelectedValue(prev => {
                    const currentValues = Array.isArray(prev) ? prev : [];
                    // Only add if it's not already in the list
                    if (!currentValues.includes(inputValue)) {
                        return [...currentValues, inputValue];
                    }
                    return currentValues;
                });

                // Clear the input value state
                setValueInputValue('');

                // Prevent the default autocomplete behavior
                event.preventDefault();
                event.stopPropagation();
            } else if (!isDropdownOpen) {
                // Only trigger search if dropdown is closed and no text is being entered
                if (onSearch) {
                    onSearch();
                }
            }
            // If dropdown is open and no text, let autocomplete handle it normally
        }
    };

    const handleNumberChange = (event) => {
        const value = event.target.value;
        // Only allow numbers and decimal points
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setSelectedValue(value);
        }
    };

    const handleBooleanChange = (event) => {
        setSelectedValue(event.target.value);
    };

    const handleDateChange = (newValue) => {
        if (isBetweenOperator) {
            setSelectedValue(prev => ({ ...prev, start: newValue }));
        } else {
            setSelectedValue(newValue);
        }
    };

    const handleEndDateChange = (newValue) => {
        setSelectedValue(prev => ({ ...prev, end: newValue }));
    };

    // New handlers for number ranges
    const handleNumberRangeStartChange = (event) => {
        const value = event.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setSelectedValue(prev => ({ ...prev, start: value }));
        }
    };

    const handleNumberRangeEndChange = (event) => {
        const value = event.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setSelectedValue(prev => ({ ...prev, end: value }));
        }
    };

    const getFieldSelectorIsDisabled = () => {
        return selectedAttribute === null || operator.KEY === "ISNULL" || operator.KEY === "NOTNULL";
    }

    const renderValueInput = () => {
        const attributeType = selectedAttribute?.type?.toLowerCase();

        switch (attributeType) {
            case 'text':
            case 'string':
                return (
                    <Mui.Box sx={{ position: 'relative', width: '100%' }}>
                        <Mui.Autocomplete
                            sx={{ width: '100%' }}
                            multiple
                            freeSolo
                            open={isDropdownOpen}
                            onOpen={() => setIsDropdownOpen(true)}
                            onClose={() => setIsDropdownOpen(false)}
                            options={
                                distinctValuesLoading
                                    ? []
                                    : (distinctValuesData ?? [])
                            }
                            getOptionLabel={(option) => {
                                // Handle both string options and object options
                                if (typeof option === 'string') return option;
                                return option.label || option.value || option;
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const label = typeof option === 'string' ? option : (option.label || option.value || option);
                                    return (
                                        <Mui.Chip
                                            variant="outlined"
                                            label={label}
                                            {...getTagProps({ index })}
                                            key={index}
                                        />
                                    );
                                })
                            }
                            renderInput={(params) => (
                                <Mui.TextField
                                    {...params}
                                    label="Select or Enter Values"
                                    onKeyDown={handleKeyDown}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {distinctValuesLoading ? (
                                                    <Mui.CircularProgress color="inherit" size={20} sx={{ position: 'absolute', right: 32 }} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            value={selectedValue || []}
                            onChange={handleValueChange}
                            inputValue={valueInputValue}
                            onInputChange={handleValueInputChange}
                            disabled={getFieldSelectorIsDisabled()}
                        />
                    </Mui.Box>
                );

            case 'bool':
            case 'boolean':
                return (
                    <Mui.FormControl component="fieldset" disabled={getFieldSelectorIsDisabled()}>
                        <Mui.RadioGroup
                            value={selectedValue}
                            onChange={handleBooleanChange}
                            row
                        >
                            <Mui.FormControlLabel
                                value="true"
                                control={<Mui.Radio />}
                                label="Yes"
                            />
                            <Mui.FormControlLabel
                                value="false"
                                control={<Mui.Radio />}
                                label="No"
                            />
                        </Mui.RadioGroup>
                    </Mui.FormControl>
                );

            case 'date':
            case 'datetime':
                return (
                    <Mui.Box sx={{ width: '100%' }}>
                        {isBetweenOperator ? (
                            <Mui.Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <DateFns.LocalizationProvider>
                                    <MuiX.DateTimePicker
                                        label="Start Date"
                                        value={selectedValue?.start ?? null}
                                        onAccept={handleDateChange}
                                        disabled={getFieldSelectorIsDisabled()}
                                        open={isStartDatePickerOpen}
                                        onOpen={() => setIsStartDatePickerOpen(true)}
                                        onClose={() => setIsStartDatePickerOpen(false)}
                                        renderInput={(params) => <Mui.TextField {...params} size="small" />}
                                    />
                                    <MuiX.DateTimePicker
                                        label="End Date"
                                        value={selectedValue?.end ?? null}
                                        onAccept={handleEndDateChange}
                                        disabled={getFieldSelectorIsDisabled()}
                                        open={isEndDatePickerOpen}
                                        onOpen={() => setIsEndDatePickerOpen(true)}
                                        onClose={() => setIsEndDatePickerOpen(false)}
                                        renderInput={(params) => <Mui.TextField {...params} size="small" />}
                                    />
                                </DateFns.LocalizationProvider>
                            </Mui.Box>
                        ) : (
                            <DateFns.LocalizationProvider>
                                <MuiX.DateTimePicker
                                    label="Select Date"
                                    value={selectedValue ?? null}
                                    onAccept={handleDateChange}
                                    disabled={getFieldSelectorIsDisabled()}
                                    open={isDatePickerOpen}
                                    onOpen={() => setIsDatePickerOpen(true)}
                                    onClose={() => setIsDatePickerOpen(false)}
                                    renderInput={(params) => <Mui.TextField {...params} />}
                                />
                            </DateFns.LocalizationProvider>
                        )}
                    </Mui.Box>
                );

            case 'number':
            case 'int':
            case 'integer':
            case 'float':
            case 'decimal':
            case 'long':
                return isBetweenOperator ? (
                    <Mui.Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Mui.TextField
                            label="From"
                            value={selectedValue?.start ?? ""}
                            onChange={handleNumberRangeStartChange}
                            type="number"
                            disabled={getFieldSelectorIsDisabled()}
                            inputProps={{
                                inputMode: 'decimal',
                                pattern: '[0-9]*\\.?[0-9]*'
                            }}
                            size="small"
                        />
                        <Mui.TextField
                            label="To"
                            value={selectedValue?.end ?? ""}
                            onChange={handleNumberRangeEndChange}
                            type="number"
                            disabled={getFieldSelectorIsDisabled()}
                            inputProps={{
                                inputMode: 'decimal',
                                pattern: '[0-9]*\\.?[0-9]*'
                            }}
                            size="small"
                        />
                    </Mui.Box>
                ) : (
                    <Mui.TextField
                        label="Enter Number"
                        value={selectedValue ?? ""}
                        onChange={handleNumberChange}
                        type="number"
                        disabled={getFieldSelectorIsDisabled()}
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '[0-9]*\\.?[0-9]*'
                        }}
                        sx={{ width: '100%' }}
                    />
                );

            default:
                // Fallback to text autocomplete with multiple values
                return (
                    <Mui.Autocomplete
                        sx={{ width: '100%' }}
                        multiple
                        freeSolo
                        open={isDropdownOpen}
                        onOpen={() => setIsDropdownOpen(true)}
                        onClose={() => setIsDropdownOpen(false)}
                        options={
                            distinctValuesLoading
                                ? []
                                : (distinctValuesData ?? [])
                        }
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option.label || option.value || option;
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const label = typeof option === 'string' ? option : (option.label || option.value || option);
                                return (
                                    <Mui.Chip
                                        variant="outlined"
                                        label={label}
                                        {...getTagProps({ index })}
                                        key={index}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (<Mui.TextField {...params} label="Select or Enter Values" onKeyDown={handleKeyDown} />)}
                        value={selectedValue || []}
                        onChange={handleValueChange}
                        inputValue={valueInputValue}
                        onInputChange={handleValueInputChange}
                        disabled={distinctValuesLoading || getFieldSelectorIsDisabled()}
                    />
                );
        }
    };



    const availableOperators = getAvailableOperators();

    return (
        <Mui.TableRow style={{ borderTop: '1px solid lightgray', borderBottom: '1px solid lightgray' }}>
            <Mui.TableCell>
                <Mui.Autocomplete
                    options={attributesData ?? []}
                    getOptionLabel={(option) => option?.label || option?.key || ''}
                    renderInput={(params) => (<Mui.TextField {...params} label="Select Attribute" />)}
                    value={selectedAttribute}
                    onChange={handleAttributeChange}
                    inputValue={attributeInputValue}
                    onInputChange={handleAttributeInputChange}
                    isOptionEqualToValue={(option, value) => option?.key === value?.key}
                />
            </Mui.TableCell>
            <Mui.TableCell style={{ borderBottom: 'none' }}>
                <Mui.FormControl fullWidth>
                    <Mui.InputLabel id={`operator-label-${rowIndex}`}>Operator</Mui.InputLabel>
                    <Mui.Select
                        id={`operator-selector-${rowIndex}`}
                        label={'Operator Label'}
                        labelId={`operator-label-${rowIndex}`}
                        value={operator}
                        onChange={handleOperatorChange}
                        sx={{ width: '100%' }}
                        renderValue={(selected) => selected?.LABEL || ''}
                        disabled={!selectedAttribute || isOperatorDisabled()} // Disable if no attribute or boolean type
                    >
                        <Mui.MenuItem value="">
                            <em>Choose one...</em>
                        </Mui.MenuItem>
                        {availableOperators.map((item) => (
                            <Mui.MenuItem key={item.KEY} value={item}>{item.LABEL}</Mui.MenuItem>
                        ))}
                    </Mui.Select>
                </Mui.FormControl>
            </Mui.TableCell>
            <Mui.TableCell align="center" style={{ borderBottom: 'none' }}>
                {renderValueInput()}
            </Mui.TableCell>
            <Mui.TableCell align="center" style={{ borderBottom: 'none' }}>
                {!!deleteRow && <Mui.IconButton aria-label="delete" size="small" disabled={false} onClick={deleteRow}>
                    <MuiIcons.Delete fontSize="small" color={'error'} />
                </Mui.IconButton>}
            </Mui.TableCell>
        </Mui.TableRow>
    )
}

export default AttributeRow